import { type TFunction } from "i18next";

import { FilterDefinition, FilterOption } from "@/components/FilterBar";

import { QUALITIES } from "../../constants";
import { getLocalisedName, translateOptions } from "../../utils/i18n";
import { OfferStatus } from "./components/OfferCard/interfaces";
import { OfferFilterKey, OfferTab } from "./types";
import { offerStatusLabelKey, tabStatuses } from "./utils";

export interface OfferFilterSources {
  tab: OfferTab;
  commodities: { id: number; name: string }[];
  aggregators: { id: number; name: string }[];
  parsedAdministrativeAreas: {
    id: number;
    name: string;
    correspondingType?: { name: string };
  }[];
}

// ExchangeAgreed is intentionally absent: it was never offered as a filter.
const FILTERABLE_STATUSES = [
  OfferStatus.Accepted,
  OfferStatus.ExchangeDone,
  OfferStatus.New,
  OfferStatus.Negotiating,
  OfferStatus.Recalled,
  OfferStatus.Rejected,
  OfferStatus.OfferDone,
  OfferStatus.ExchangeDetailsUpdated,
  OfferStatus.ExchangeDetailsDisputed,
  OfferStatus.ExchangePaymentPending,
];

export const buildOfferFilterDefinitions = (
  t: TFunction,
  { tab, commodities, aggregators, parsedAdministrativeAreas }: OfferFilterSources
): FilterDefinition<OfferFilterKey>[] => [
  {
    key: "aggregator_id",
    label: t("filters.aggregator"),
    searchable: true,
    options: (aggregators || []).map(aggregator => ({
      value: aggregator.id.toString(),
      label: aggregator.name,
    })),
  },
  {
    key: "location_id",
    label: t("filters.location"),
    searchable: true,
    options: (parsedAdministrativeAreas || []).map(area => ({
      value: area.id.toString(),
      label: area.correspondingType?.name
        ? `${area.name} (${area.correspondingType.name})`
        : area.name,
    })),
  },
  {
    key: "status",
    label: t("filters.status"),
    searchable: true,
    options: FILTERABLE_STATUSES.filter(status => tabStatuses(tab).some(id => id === status)).map(
      status => ({
        value: status,
        label: t(offerStatusLabelKey(status)),
      })
    ),
  },
  {
    key: "commodity_id",
    label: t("filters.commodity"),
    searchable: true,
    options: (commodities || []).map(commodity => ({
      value: commodity.id.toString(),
      label: getLocalisedName(commodity),
    })),
  },
  {
    key: "quality",
    label: t("filters.quality"),
    options: translateOptions(QUALITIES, t).map((option: FilterOption) => ({
      value: option.value,
      label: option.label,
    })),
  },
];
